// Money DISPLAY formatting — the only thing the client does with money.
// Every money/percent value arrives from Rails as a decimal string
// ("6043.95", "0.034", "24.0"); these helpers format for display and never
// feed anything back into arithmetic. editor-core stays money-free by
// charter (worksheet.ts) — bid components import from here instead.
// The one sanctioned client money math lives in lib/bid-what-if.ts.

export const EM_DASH = "—"

/** "$6,043.95" — fixed 2dp currency. null/undefined → em dash. */
export function money(value: string | null | undefined): string {
  if (value == null || value === "") return EM_DASH
  const n = Number(value)
  if (!Number.isFinite(n)) return EM_DASH
  return usd(2).format(n)
}

/**
 * A unit rate, keeping the wire string's own precision (min 2dp, max 4):
 * "0.8" → "$0.80", "0.034" → "$0.034". With a unit, "$1.104/SF".
 */
export function rate(value: string | null | undefined, unit?: string): string {
  if (value == null || value === "") return EM_DASH
  const n = Number(value)
  if (!Number.isFinite(n)) return EM_DASH
  const decimals = Math.min(4, Math.max(2, value.split(".")[1]?.length ?? 0))
  const formatted = usd(decimals).format(n)
  return unit ? `${formatted}/${unit}` : formatted
}

/** "24.0%" — one decimal place. null → em dash (an uncomputable margin). */
export function pct(value: string | null | undefined): string {
  if (value == null || value === "") return EM_DASH
  const n = Number(value)
  if (!Number.isFinite(n)) return EM_DASH
  return `${n.toFixed(1)}%`
}

/** "+1.0 pt" / "−1.0 pt" — a margin-point delta. */
export function pctDelta(value: string | null | undefined): string {
  if (value == null || value === "") return EM_DASH
  const n = Number(value)
  if (!Number.isFinite(n)) return EM_DASH
  const sign = n < 0 ? "−" : "+"
  return `${sign}${Math.abs(n).toFixed(1)} pt`
}

/**
 * Badge tone for a GM against its target: at/above target reads accent
 * (healthy), below reads signal (attention). Comparison only — never
 * arithmetic on money.
 */
export function gmTone(
  actual: string | null | undefined,
  target: string | null | undefined
): "accent" | "signal" {
  if (actual == null || actual === "" || target == null || target === "") return "signal"
  const a = Number(actual)
  const t = Number(target)
  if (!Number.isFinite(a) || !Number.isFinite(t)) return "signal"
  return a >= t ? "accent" : "signal"
}

const formatters = new Map<number, Intl.NumberFormat>()

function usd(decimals: number): Intl.NumberFormat {
  let cached = formatters.get(decimals)
  if (!cached) {
    cached = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    formatters.set(decimals, cached)
  }
  return cached
}
